import * as THREE from 'three'
import {MercatorCoordinate} from 'maplibre-gl'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js'
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js'
import {BaseLoader} from './BaseLoader'
import {metersBetweenMercator} from '../mapUtils'

const DRONE_MODEL_PATHS = {
  drone1: 'models/drones/drone1.glb',
  drone2: 'models/drones/drone2.glb',
  drone3: 'models/drones/drone3.glb',
} as const

const DRONE_DEFAULT_ALTITUDE = 30
const DRONE_DEFAULT_SCALE = 0.5
const DRONE_DEFAULT_SPEED = 0.0002
const DRONE_HITBOX_RADIUS = 12

export type DroneType = keyof typeof DRONE_MODEL_PATHS

const DRONE_MODEL_SCALES: Record<DroneType, number> = {
  drone1: 1.5,
  drone2: 1.5,
  drone3: 0.5,
}

export interface DroneConfig {
  path: [number, number][]
  type?: DroneType
  speed?: number
  altitude?: number
  modelScale?: number
}

interface ModelAsset {
  scene: THREE.Group
  animations: THREE.AnimationClip[]
}

interface DroneState {
  id: string
  path: [number, number][]
  segmentIndex: number
  progress: number
  speed: number
  altitude: number
  modelScale: number
  type: DroneType
  wrapper: THREE.Group
  hitbox: THREE.Mesh
  mixer: THREE.AnimationMixer | null
  currentLngLat: [number, number]
}

export class DroneLoader extends BaseLoader {
  private readonly gltfLoader = new GLTFLoader()
  private readonly modelCache = new Map<DroneType, ModelAsset>()
  private readonly loadingPromises = new Map<DroneType, Promise<ModelAsset>>()
  private readonly droneStates = new Map<string, DroneState>()
  private lastTime = performance.now() * 0.001

  async waitForLoad(): Promise<void> {
    return Promise.resolve()
  }

  addDrone(id: string, config: DroneConfig): void {
    if (!this.hasThreeLayer()) return

    const {
      path,
      type = 'drone1',
      speed = DRONE_DEFAULT_SPEED,
      altitude = DRONE_DEFAULT_ALTITUDE,
      modelScale = DRONE_MODEL_SCALES[type ?? 'drone1'] ?? DRONE_DEFAULT_SCALE,
    } = config

    const wrapper = new THREE.Group()
    wrapper.name = `drone-${id}`
    this.threeLayer.addRawObject(wrapper)

    const hitbox = new THREE.Mesh(
      new THREE.SphereGeometry(DRONE_HITBOX_RADIUS, 8, 8),
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
      })
    )
    hitbox.name = `drone-hitbox-${id}`
    hitbox.userData.droneId = id
    wrapper.add(hitbox)

    const state: DroneState = {
      id,
      path,
      segmentIndex: 0,
      progress: 0,
      speed,
      altitude,
      modelScale,
      type,
      wrapper,
      hitbox,
      mixer: null,
      currentLngLat: [...path[0]],
    }
    this.droneStates.set(id, state)

    this.getOrLoadModel(type)
      .then((asset) => {
        const s = this.droneStates.get(id)
        if (!s) return

        const model = SkeletonUtils.clone(asset.scene) as THREE.Group
        model.position.set(0, 0, 0)
        model.rotation.set(0, 0, 0)
        model.scale.setScalar(s.modelScale)

        if (asset.animations.length > 0) {
          const mixer = new THREE.AnimationMixer(model)
          for (const clip of asset.animations) {
            const posTrackedNames = new Set(
              clip.tracks
                .filter((t) => t.name.endsWith('.position'))
                .map((t) => t.name.slice(0, t.name.lastIndexOf('.')))
            )
            if (posTrackedNames.size > 0) {
              const queue: THREE.Object3D[] = [...model.children]
              let found = false
              while (queue.length && !found) {
                const node = queue.shift()!
                if (posTrackedNames.has(node.name)) {
                  node.position.set(0, 0, 0)
                  found = true
                } else {
                  node.children.forEach((c) => queue.push(c))
                }
              }
            }
            const filteredTracks = clip.tracks.filter(
              (t) => !t.name.endsWith('.position')
            )
            if (filteredTracks.length === 0) continue
            mixer
              .clipAction(
                new THREE.AnimationClip(
                  clip.name,
                  clip.duration,
                  filteredTracks
                )
              )
              .play()
          }
          s.mixer = mixer
        }

        s.wrapper.add(model)
      })
      .catch((err) =>
        console.error(`DroneLoader: ошибка загрузки "${type}"`, err)
      )
  }

  removeDrone(id: string): void {
    const state = this.droneStates.get(id)
    if (state) {
      state.mixer?.stopAllAction()
      state.hitbox.geometry.dispose()
      ;(state.hitbox.material as THREE.Material).dispose()
      this.threeLayer?.removeRawObject(state.wrapper)
    }
    this.droneStates.delete(id)
  }

  getHitboxes(): {id: string; hitbox: THREE.Object3D}[] {
    const result: {id: string; hitbox: THREE.Object3D}[] = []
    this.droneStates.forEach((state) => {
      result.push({id: state.id, hitbox: state.hitbox})
    })
    return result
  }

  getDrones(): {id: string; lngLat: [number, number]}[] {
    const result: {id: string; lngLat: [number, number]}[] = []
    this.droneStates.forEach((state) => {
      result.push({id: state.id, lngLat: [...state.currentLngLat]})
    })
    return result
  }

  clearAllDrones(): void {
    // @ts-ignore
    ;[...this.droneStates.keys()].forEach((id) => this.removeDrone(id))
  }

  update(currentTime: number): void {
    const dt = Math.min(currentTime - this.lastTime, 0.1)
    this.lastTime = currentTime

    const originLngLat = this.threeLayer.getSceneOrigin()
    if (!originLngLat) return

    const originMercator = MercatorCoordinate.fromLngLat(
      {lng: originLngLat[0], lat: originLngLat[1]},
      0
    )

    this.droneStates.forEach((state) => {
      state.mixer?.update(dt)
      this.moveDrone(state, dt)
      this.applyPosition(state, originMercator)
    })
  }

  private moveDrone(state: DroneState, dt: number): void {
    const {path} = state
    const from = path[state.segmentIndex]
    const toIdx = (state.segmentIndex + 1) % path.length
    const to = path[toIdx]

    const segLen = Math.sqrt((to[0] - from[0]) ** 2 + (to[1] - from[1]) ** 2)
    state.progress += (state.speed * dt) / Math.max(segLen, 1e-9)

    if (state.progress >= 1) {
      state.segmentIndex = toIdx
      state.progress = 0
    }

    const cur = path[state.segmentIndex]
    const nextIdx = (state.segmentIndex + 1) % path.length
    const next = path[nextIdx]
    const t = state.progress

    state.currentLngLat = [
      cur[0] + (next[0] - cur[0]) * t,
      cur[1] + (next[1] - cur[1]) * t,
    ]

    const bearing = Math.atan2(next[0] - cur[0], next[1] - cur[1])
    state.wrapper.rotation.y = bearing
  }

  private applyPosition(
    state: DroneState,
    originMercator: MercatorCoordinate
  ): void {
    const objMercator = MercatorCoordinate.fromLngLat(
      {lng: state.currentLngLat[0], lat: state.currentLngLat[1]},
      state.altitude
    )
    const {dEastMeter, dNorthMeter} = metersBetweenMercator(
      originMercator,
      objMercator
    )
    state.wrapper.position.set(dEastMeter, state.altitude, dNorthMeter)
  }

  private getOrLoadModel(type: DroneType): Promise<ModelAsset> {
    const cached = this.modelCache.get(type)
    if (cached) return Promise.resolve(cached)

    const existing = this.loadingPromises.get(type)
    if (existing) return existing

    const promise = new Promise<ModelAsset>((resolve, reject) => {
      this.gltfLoader.load(
        DRONE_MODEL_PATHS[type],
        (gltf) => {
          const asset: ModelAsset = {
            scene: gltf.scene,
            animations: gltf.animations,
          }
          this.modelCache.set(type, asset)
          resolve(asset)
        },
        undefined,
        reject
      )
    })
    this.loadingPromises.set(type, promise)
    return promise
  }
}
